import React from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  Button,
  Form,
  FormField,
  Label,
  Input,
  Select,
  AddableSelect,
  NumberInput,
} from '@/components/ui-primitives/small'
import { Alert, AlertDescription } from '@/components/ui-primitives/alert'
import { BrushCleaning, Info } from 'lucide-react'
import { CodeEditor } from '@/components/editor/code-editor'
import { ACE_THEME_NAMES, AceThemeName } from '@/components/editor/code-editor/ace-themes'
import { queryClient } from '@/queryClient'
import Command from "@/helpers/Command"
import { toast } from "sonner"
import { isMacOS } from "@/utils/operating-system"
import { useFeatureFlags } from '@/flags'
import { Switch } from '@/components/ui-primitives/switch'

type LocalSettings = {
  fontFamily: string
  fontSize: string
  fontSizeDocked: string
  fontWeight: string
  editorHeightDocked: string
  sparqlEditor: "yasqe" | "ace"
  liveUpdateDelay: number
  aceTheme: AceThemeName | null
  /** Whether advanced widget options always show; null follows the deployment's default (`basicWidgetSettingsByDefault`) */
  showAdvancedWidgetSettings: boolean | null
  queryChipsHintDismissed: boolean
}

const defaults: LocalSettings = {
  fontFamily: "Inconsolata Variable",
  fontSize: "17px",
  fontSizeDocked: "15px",
  fontWeight: "500",
  editorHeightDocked: "120px",
  sparqlEditor: "yasqe",
  liveUpdateDelay: 0.2,
  aceTheme: null,
  showAdvancedWidgetSettings: null,
  queryChipsHintDismissed: false,
} as const

export const useLocalSettings = create<LocalSettings>()(persist(() => defaults, {
  name: 'local-settings-store-v1'
}))

/** Whether advanced widget options always show: the user's own choice, else the deployment's default */
export function useAlwaysShowAdvanced(): boolean {
  const basicByDefault = useFeatureFlags().basicWidgetSettingsByDefault
  const choice = useLocalSettings(s => s.showAdvancedWidgetSettings)
  return choice ?? !basicByDefault
}

export function setAlwaysShowAdvanced(show: boolean) {
  useLocalSettings.setState({ showAdvancedWidgetSettings: show })
}

export function LocalSettings() {
  const localSettings = useLocalSettings()
  const alwaysShowAdvanced = useAlwaysShowAdvanced()

  return (
    <Form>
      <Alert className="p-2 bg-blue-100 text-blue-900">
        <AlertDescription className="flex gap-2 items-center text-xs">
          <Info size={14} />
          These settings are stored locally in your browser.
        </AlertDescription>
      </Alert>
      <FormField className="gap-1">
        <div className="flex items-center gap-2.5">
          <Switch id="advanced-widget-settings"
                  checked={alwaysShowAdvanced}
                  onCheckedChange={setAlwaysShowAdvanced}
                  className="data-[state=checked]:bg-[#51479e]" />
          <Label htmlFor="advanced-widget-settings"
                 className="w-auto cursor-pointer text-[13px] text-foreground">
            Always show advanced widget options
          </Label>
        </div>
        <span className="text-xs text-muted-foreground">
          Options most configurations don't need, such as extra topic wiring and detailed styling.
          When off, each tab hides them behind a Show link.
        </span>
      </FormField>
      <FormField>
        <Label>Preview</Label>
        <CodeEditor
          mode="sparql"
          name="Preview"
          showGutter={false}
          value={`
# 0123456789 !@#$%^&*()_+-=[]{}|;:'",.<>/?
PREFIX : <the://quick.brown.fox/jumps-over/the-lazy-dog>
SELECT ?fox
WHERE {
   ?fox :jumpsOver ?dog .
   ?fox :speed "quick" .
   ?fox :color "brown" .
   ?dog :hasAttribute :lazy .
}
          `.trim()}
          />
      </FormField>
      <FormField>
        <Label>Code editor font</Label>
        <AddableSelect
              value={localSettings.fontFamily}
              onChange={value => useLocalSettings.setState({ fontFamily: value })}>
          <optgroup label="Wistor platform fonts">
            <option>Inconsolata Variable</option>
            <option>Cascadia Code</option>
          </optgroup>
          <optgroup label="System fonts">
            {isMacOS && (
              <option>Monaco </option>
            )}
            <option>Consolas</option>
            <option>Courier New</option>
          </optgroup>
        </AddableSelect>
      </FormField>
      <FormField>
        <Label>Code editor theme (only applicable to ACE)</Label>
        <Select value={localSettings.aceTheme ?? ""}
                onChange={e => useLocalSettings.setState({
                  aceTheme: (e.currentTarget.value || null) as AceThemeName | null
                })}>
          <option value="">Default</option>
          {ACE_THEME_NAMES.map(theme => (
            <option key={theme} value={theme}>{theme}</option>
          ))}
        </Select>
      </FormField>
      <FormField>
        <Label>Font size (docked)</Label>
        <Input type="text"
               value={localSettings.fontSizeDocked}
               onChange={e => useLocalSettings.setState({ fontSizeDocked: e.currentTarget.value })} />
      </FormField>
      <FormField>
        <Label>Font size (floating)</Label>
        <Input type="text"
               value={localSettings.fontSize}
               onChange={e => useLocalSettings.setState({ fontSize: e.currentTarget.value })} />
      </FormField>
      <FormField>
        <Label>Font weight</Label>
        <Select value={localSettings.fontWeight}
                onChange={e => useLocalSettings.setState({ fontWeight: e.currentTarget.value })}>
          <option value="300">light</option>
          <option value="400">normal</option>
          <option value="500">semibold</option>
          <option value="600">bold</option>
        </Select>
      </FormField>
      <FormField>
        <Label>Default code editor height (docked)</Label>
        <Input type="text"
               value={localSettings.editorHeightDocked}
               onChange={e => useLocalSettings.setState({ editorHeightDocked: e.currentTarget.value })} />
      </FormField>
      <FormField>
        <Label>SPARQL editor (only in floating mode)</Label>
        <Select value={localSettings.sparqlEditor}
                onChange={e => useLocalSettings.setState({ sparqlEditor: e.currentTarget.value as "yasqe" | "ace" })}>
          <option value="yasqe">YASQE</option>
          <option value="ace">ACE</option>
        </Select>
      </FormField>
      <FormField>
        <Label>Default code editor height (docked)</Label>
        <Input type="text"
               value={localSettings.editorHeightDocked}
               onChange={e => useLocalSettings.setState({ editorHeightDocked: e.currentTarget.value })} />
      </FormField>
      <FormField>
        <Label>Time in seconds before applying changes to the live app preview and undo history</Label>
        <NumberInput pattern="[0-9]+([.,][0-9]+)?"
                     value={localSettings.liveUpdateDelay}
                     onChange={value => (value != null) && useLocalSettings.setState({ liveUpdateDelay: value })} />
      </FormField>
      <FormField>
        <Button variant="outline"
                className="flex-shrink"
                onClick={() => useLocalSettings.setState(defaults)}>
          Reset all to default
        </Button>
      </FormField>
      <div className="border-b border-gray-200 mt-1 mb-0.5" />
      <FormField>
        <Label>Cache utilities</Label>
        <Button variant="outline"
                onClick={() => {
                  queryClient.invalidateQueries()
                  toast.info("Cache cleaned")
                }}>
          Invalidate client-side query cache
        </Button>
        <Button variant="outline"
                onClick={() => {
                  toast.promise(Command.deleteServerCache(), {
                    loading: "Invalidating server cache...",
                    success: "Successfully deleted server cache",
                    error: e => `Failed to delete server cache: ${e}`
                  })
                }}>
          Invalidate server-side cache
        </Button>
      </FormField>
    </Form>
  )
}
